import { Plus } from 'lucide-react';
import { Button, PageHeader, Select } from '@/shared/components';
import { CleaningDateNav, dayTitle } from '../CleaningDateNav';
import { CleaningScopePicker } from '../CleaningScopeRequired';
import { addDaysIso, weekRangeLabel } from '../calendar/cleaningCalendarHelpers';
import type { CleaningDayFilter } from '../day/cleaningDayModel';
import type { useCleaningBoardQueries } from '../useCleaningBoardQueries';
import type { useCleaningScope } from '../useCleaningScope';
import { BOARD_FILTERS, boardFacts, filterCount, type CleaningBoardView } from './boardModel';

interface BoardSearch {
  date?: string;
  status?: Exclude<CleaningDayFilter, 'all'>;
  assignee?: string;
}

/**
 * Wald-Kopf der Aufgaben (Basis-Stil): Datum auf dem Wald, „Aufgabe“ als Aktion, Status als
 * Reiter mit Zahl aus der geladenen Tagesliste; Objekt und Person darunter.
 */
export function BoardHeader({
  view,
  date,
  today,
  filter,
  assignee,
  queries,
  scope,
  mayManage,
  onSearch,
  onGroup,
  onCreate,
}: {
  view: CleaningBoardView;
  date: string;
  today: string;
  filter: CleaningDayFilter;
  assignee: string;
  queries: ReturnType<typeof useCleaningBoardQueries>;
  scope: ReturnType<typeof useCleaningScope>;
  mayManage: boolean;
  onSearch: (patch: BoardSearch) => void;
  onGroup: (id: string | null) => void;
  onCreate: () => void;
}) {
  const week = view === 'calendar';
  const [weekStart = date, weekEnd = date] = [queries.weekDates[0], queries.weekDates[6]];
  // Reiterzahlen gehören zur sichtbaren (nach Person gefilterten) Liste, der Satz zur Lage des Tages.
  const tasks = queries.dayQuery.data;
  const goTo = (next: string) => onSearch({ date: next === today ? undefined : next });
  const showPicker = scope.usesLocalScope && scope.groups.length > 1 && scope.group;
  return (
    <>
      <PageHeader
        className="mb-4"
        title={week ? 'Woche' : date === today ? 'Aufgaben heute' : 'Aufgaben'}
        description={
          week ? scope.group?.name : boardFacts(queries.allDayQuery.data, date, date === today)
        }
        tools={
          <CleaningDateNav
            label={week ? weekRangeLabel(weekStart, weekEnd) : dayTitle(date, today)}
            unit={week ? 'week' : 'day'}
            date={date}
            showToday={week ? !queries.weekDates.includes(today) : date !== today}
            onShift={(direction) => goTo(addDaysIso(date, direction * (week ? 7 : 1)))}
            onPick={goTo}
            onToday={() => goTo(today)}
          />
        }
        actions={
          mayManage ? (
            <Button
              variant="brand"
              className="min-h-11"
              leftIcon={<Plus size={16} aria-hidden />}
              onClick={onCreate}
            >
              Aufgabe
            </Button>
          ) : undefined
        }
        filterTabsLabel="Status"
        filterTabs={
          week
            ? undefined
            : BOARD_FILTERS.map((option) => ({
                label: option.label,
                active: filter === option.value,
                count: filterCount(tasks, option.value),
                onSelect: () =>
                  onSearch({ status: option.value === 'all' ? undefined : option.value }),
              }))
        }
      />
      {(showPicker || mayManage) && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          {showPicker && scope.group && (
            <CleaningScopePicker
              groups={scope.groups}
              selectedId={scope.group.id}
              select={onGroup}
            />
          )}
          {mayManage && (
            <label className="text-ink-soft flex min-w-56 flex-col gap-1 text-xs font-semibold">
              Verantwortlich
              <Select
                value={assignee}
                onChange={(event) => onSearch({ assignee: event.target.value || undefined })}
                className="min-h-11"
              >
                <option value="">Alle</option>
                {queries.people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </Select>
            </label>
          )}
        </div>
      )}
    </>
  );
}
